import { useCallback } from 'react'
import { IFCMode, useSelectionStore } from '@editorStores'

const IFC_MODE_TO_EDITOR_MODE: Record<Exclude<IFCMode, null>, EditorMode> = {
  building: 'ifc-building',
  settings: 'ifc-settings',
  add: 'ifc-add',
  guided: 'ifc-guided',
  alignment: 'ifc-alignment',
  filter: 'ifc-filter',
  storey: 'ifc-storey',
  type: 'ifc-type',
  'draw-roofs': 'ifc-draw-roofs',
  'draw-slabs': 'ifc-draw-slabs',
  'draw-walls': 'ifc-draw-walls',
  'draw-openings': 'ifc-draw-openings',
  'auto-connect': 'ifc-auto-connect',
}

const EDITOR_MODE_TO_IFC_MODE = Object.fromEntries(
  Object.entries(IFC_MODE_TO_EDITOR_MODE).map(([ifcMode, editorMode]) => [editorMode, ifcMode]),
) as Partial<Record<EditorMode, Exclude<IFCMode, null>>>

/**
 * IFC import tool mode backed by the global selection store — drop-in replacement for the former
 * `controlStore.ifcMode`/`setIfcMode`. The IFC modes are ordinary `EditorMode`s (`'ifc-*'`), so
 * "exactly one mode at all times" holds across pages and leaving the IFC page resets them like
 * any other page-bound mode.
 */
const useIfcMode = () => {
  const mode = useSelectionStore(state => state.mode)
  const switchMode = useSelectionStore(state => state.switchMode)

  const ifcMode: IFCMode = EDITOR_MODE_TO_IFC_MODE[mode] ?? null

  const setIfcMode = useCallback(
    (next: IFCMode) => switchMode(next ? IFC_MODE_TO_EDITOR_MODE[next] : 'neutral'),
    [switchMode],
  )

  return { ifcMode, setIfcMode }
}

export default useIfcMode
